import { AnimatePresence, motion, Variants } from 'framer-motion';
import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  useId,
  useMemo,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import {
  FocusScope,
  _mergeProps,
  _attemptFocus,
  _getFirstFocusableChild,
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';
import { _useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Heading } from '@dynatrace/strato-components/typography';
import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';

import { calculateBreakpointFromContainerSize } from './sheet-container-utils.js';
import {
  sheetBackdropCSS,
  sheetCSS,
  sheetHeadingCSS,
  sheetScrollableContainerCSS,
  sheetWrapperCSS,
} from './Sheet.sty.js';
import { OverlayContainer } from '../../core/components/OverlayContainer.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import { useDialog } from '../hooks/useDialog.js';
import { useOverlay } from '../hooks/useOverlay.js';
/**
 * @public
 */
export interface SheetProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  /** The title which is displayed at the top of the sheet. */
  title?: string;

  /**
   * Toggles the visibility of the sheet component.
   * @defaultValue false
   */
  show?: boolean;

  /**
   * The offset to the top of the App which should not be covered.
   * @defaultValue 28
   */
  topoffset?: number;

  /**
   * Actions will be rendered to the top right, next to the title.
   * They usually contain buttons like 'Apply' or 'Cancel'.
   */
  actions?: ReactNode;

  /** Handler that is only called if the sheet is closed with the Escape key. */
  onDismiss?: () => void;
}

/** Animation variants */
const getAnimationVariants = (topOffset: number): Record<string, Variants> => ({
  backdrop: {
    enterBackdrop: {
      // For the opacity we need to use the default value here. Framer motion would complain
      // about too complex values, when transitioning from two custom property values within
      // an rgba value apparently.
      opacity: 1,
      transition: {
        duration: AnimationsJS.FadeIn.Duration.Fast,
        ease: AnimationsJS.FadeIn.Easing,
      },
    },
    exitBackdrop: {
      opacity: 0,
      transition: {
        duration: AnimationsJS.FadeOut.Duration.Fast,
        ease: AnimationsJS.FadeOut.Easing,
        delay: AnimationsJS.FadeOut.Duration.Base,
      },
    },
  },
  sheet: {
    enterSlide: {
      top: topOffset,
      opacity: 1,
      transition: {
        duration: AnimationsJS.SlideIn.Duration.Slow,
        ease: AnimationsJS.SlideIn.Easing,
      },
    },
    exitSlide: () => ({
      top: `calc(${topOffset}px + 10%)`,
      opacity: 0.3,
      transition: {
        duration: AnimationsJS.SlideOut.Duration.Slow,
        ease: AnimationsJS.SlideOut.Easing,
      },
    }),
  },
  content: {
    enterFade: {
      opacity: 1,
      transition: {
        duration: AnimationsJS.SlideIn.Duration.Slow,
        ease: AnimationsJS.FadeIn.Easing,
      },
    },
    exitFade: {
      opacity: 0,
      transition: {
        duration: AnimationsJS.SlideOut.Duration.Slow,
        ease: AnimationsJS.FadeOut.Easing,
      },
    },
  },
});

/**
 * The `Sheet` component allows you to show additional content in an overlay of your
 * application. It is essential to add at least one focusable element
 * inside the `Sheet` to be keyboard accessible. If none is available the
 * focus will be lost and given to the `window.document` when opening the
 * sheet.
 *
 * The developer is responsible for the sheet's visibility via
 * the `show` prop. This prop must be updated to false in the `onDismiss`
 * callback to ensure keyboard accessibility.
 * @public
 */
export const Sheet = /* @__PURE__ */ forwardRef<HTMLDivElement, SheetProps>(
  (props, forwardedRef) => {
    const {
      show = false,
      title,
      children,
      onDismiss,
      actions,
      topoffset = 28,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const { 'aria-labelledby': ariaLabelledBy, ...ariaLabelingProps } =
      _useAriaLabelingProps(remainingProps);

    const ref = useRef(null);
    const animationVariants = getAnimationVariants(topoffset);

    // adds all aria labels to dialog as well as tabindex & role
    const { dialogProps } = useDialog(remainingProps);

    // Handle interacting outside the dialog and pressing the Escape key to
    // close the sheet.
    const { overlayProps } = useOverlay(
      {
        ...props,
        onClose: () => {
          onDismiss?.();
        },
        isOpen: show,
        isDismissible: false,
        isKeyboardDismissDisabled: false,
      },
      ref,
    );

    // Scope the passed children to be able to focus the first element of children
    const childrenScopeRef = useRef<HTMLDivElement>(null);
    const actionScopeRef = useRef<HTMLDivElement>(null);

    const focusFirstChildElement = () => {
      if (childrenScopeRef.current && actionScopeRef.current) {
        let firstFocusableChild = _getFirstFocusableChild(
          childrenScopeRef.current,
        );

        // If we do not find an interactive element in the children
        // search for one in the actions.
        if (!firstFocusableChild) {
          firstFocusableChild = _getFirstFocusableChild(actionScopeRef.current);
        }

        firstFocusableChild &&
          _attemptFocus(firstFocusableChild as HTMLElement);
      }
    };

    // Get the ref from the grid container to calculate its size.
    const containerRef = useRef<HTMLDivElement>(null);

    const containerSize = useContainerSize(containerRef);

    const mergedRef = useMergeRefs([containerRef, forwardedRef]);

    const breakpointStyle = useMemo<CSSProperties>(
      () => calculateBreakpointFromContainerSize(containerSize),
      [containerSize],
    );

    const overlayContainerProps = useOverlayStacking(0, 'overlay');
    const headingId = useId();

    return (
      <OverlayContainer
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        style={{ ...overlayContainerProps.style, ...consumerStyle }}
        className={consumerClassName}
      >
        <AnimatePresence>
          {show && (
            <motion.div
              // Within this container the animation is rendered. The applied
              // style contains an important overflow: hidden which prevents
              // ugly moving scrollbars during the animation.
              data-testid="sheet-overlay"
              variants={animationVariants.backdrop}
              initial="exitBackdrop"
              animate="enterBackdrop"
              exit="exitBackdrop"
              className={sheetWrapperCSS}
            >
              <div
                // Transparent div which prevents the user from clicking
                // main page elements on the left or right of the sheet.
                data-testid="sheet-backdrop"
                ref={mergedRef}
                onPointerDown={(event: ReactMouseEvent) => {
                  if (event.target === event.currentTarget) {
                    // The useOverlay hook keeps an internal state of the stacked overlays.
                    // However, some overlays don't rely on the hook; hence the list isn't complete
                    // and the overlay is dismissed when clicking on any not registered overlay.
                    // (e.g. Microguide would not influence the dismissing but can't be added to
                    // the list either, because it is always on top and shouldn't be closed)
                    // Therefore, the sheet is manually dismissed on backdrop click.
                    // This topic will be revisited and aligned with the Overlay Rework (APPDEV-3373).
                    onDismiss?.();
                  }
                }}
                className={sheetBackdropCSS}
              >
                <FocusScope
                  // If no focusable children are provided the default focus
                  // is on the actions. This makes sense anyways and more
                  // importantly is needed to even make a sheet without
                  // focusable children dismissible with the Escape key.
                  contain
                >
                  <motion.div
                    // The react-aria spread needs to be cast to Record<string, any> as there
                    // is a mismatch between lib.dom HTMLElement and the
                    // react-synthetic event and spreads can only be applied on objects.
                    {...(_mergeProps(overlayProps, dialogProps, {
                      style: breakpointStyle,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    }) as Record<string, any>)}
                    className={sheetCSS}
                    data-ismodal={true}
                    data-testid="sheet"
                    variants={animationVariants.sheet}
                    initial="exitSlide"
                    animate="enterSlide"
                    exit="exitSlide"
                    onAnimationComplete={() => {
                      show && focusFirstChildElement();
                      window.dispatchEvent(new Event('animationDone'));
                    }}
                    ref={ref}
                    aria-labelledby={ariaLabelledBy ?? headingId}
                    {...ariaLabelingProps}
                  >
                    <motion.div
                      className={sheetScrollableContainerCSS}
                      style={{ height: `calc(100% - ${topoffset}px)` }}
                      variants={animationVariants.content}
                      initial="exitFade"
                      animate="enterFade"
                      exit="exitFade"
                    >
                      <OverlayStackingProvider>
                        <Flex gap={16} flexDirection="column" height="100%">
                          {(title || actions) && (
                            <Flex
                              justifyContent={
                                title ? 'space-between' : 'flex-end'
                              }
                              alignItems="flex-start"
                            >
                              {title && (
                                <Heading
                                  level={5}
                                  as="h2"
                                  id={headingId}
                                  className={sheetHeadingCSS}
                                >
                                  {title}
                                </Heading>
                              )}
                              {actions && (
                                <Flex
                                  flexWrap="wrap"
                                  justifyContent="flex-end"
                                  ref={actionScopeRef}
                                >
                                  {actions}
                                </Flex>
                              )}
                            </Flex>
                          )}
                          <div
                            ref={childrenScopeRef}
                            style={{
                              position: 'relative',
                              height: '100%',
                              minHeight: 0,
                            }}
                          >
                            {children}
                          </div>
                        </Flex>
                      </OverlayStackingProvider>
                    </motion.div>
                  </motion.div>
                </FocusScope>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </OverlayContainer>
    );
  },
);
(Sheet as typeof Sheet & { displayName: string }).displayName = 'Sheet';
